import { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useApi } from 'model/Api';
import { Ledger, ledgerPaymentStatusList, ledgerStatusList, manifestoTypeList, safetyDocumentTypeList } from 'model/Ledger';
import { generalStyle } from 'helper/Style';
import { convertCosts, convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import H2 from 'component/part/H2';

const resourceUrl = process.env.REACT_APP_RESOURCE_ENDPOINT || '';

const style = {
  layout: {
    display: 'grid',
    gridTemplateColumns: '40rem auto',
    gap: '4rem',
    width: 'fit-content',
    fontSize: '1.6rem',
  },
  dateArea: {
    display: 'flex',
    gap: '4rem',
  },
  variableListArea: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '1rem',
    ['& > div']: {
      display: 'contents',
    },
  },
  resourceArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  resourceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    border: 'solid 0.1rem #cccccc',
    borderRadius: '0.5rem',
    padding: '1rem',
    ['& img']: {
      maxWidth: '10rem',
      maxHeight: '10rem',
    },
  },
  costArea: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    ['& > div']: {
      display: 'contents',
      ['& > div']: {
        borderBottom: 'solid 0.1rem #cccccc',
        padding: '1rem 0',
      },
      ['& > div:nth-of-type(2n)']: {
        textAlign: 'right' as const,
        paddingLeft: '1rem',
      },
    },
  },
};

const LedgerShow: React.FC = () => {
  const api = useApi();
  const urlParams = useParams<{ id: string }>();
  const ledgerId = parseInt(urlParams.id || '');
  const [ledger, setLedger] = useState<Ledger>();
  const safetyDocumentType = safetyDocumentTypeList.find(({ value }) => value === ledger?.safetyDocumentType);
  const safetyDocumentTypeLabel = safetyDocumentType?.value === 'other'
    ? (ledger?.safetyDocumentTypeText || '')
    : (safetyDocumentType?.label || '');
  const manifestoType = manifestoTypeList.find(({ value }) => value === ledger?.manifestoType);
  const status = ledgerStatusList.find(({ value }) => value === ledger?.status);
  const paymentStatus = ledgerPaymentStatusList.find(({ value }) => value === ledger?.paymentStatus);
  const resources = (ledger?.resources || []).filter(({ detail }) => detail?.summary !== 'flow');
  const flowResource = (ledger?.resources || []).find(({ detail }) => detail?.summary === 'flow');
  const totalCosts = convertCosts(ledger?.costs);
  const grossProfit = (ledger?.price || 0) - totalCosts.total;

  useEffect(() => {
    (async () => {
      const ledgerResponse = await api.ledger.get(
        ledgerId,
        { with: ['leader', 'safetyOfficer', 'primeContractor', 'resource', 'member', 'cost'] }
      );
      if (ledgerResponse) {
        setLedger(ledgerResponse);
      }
    })();
  }, [ledgerId]);

  return <Layout>
    <BackLink link='/ledger/list' label='台帳一覧'></BackLink>
    <H2>台帳</H2>
    <div css={style.layout}>
      <div css={generalStyle.form}>
        <FormContent label='現場名'>
          <div css={generalStyle.item()}>{ledger?.name || ''}</div>
        </FormContent>
        <FormContent label='現場名略称'>
          <div css={generalStyle.item()}>{ledger?.shortName || ''}</div>
        </FormContent>
        <FormContent label='現場住所'>
          <div css={generalStyle.item()}>{ledger?.address || ''}</div>
        </FormContent>
        <div css={style.dateArea}>
          <FormContent label='開始日'>
            <div css={generalStyle.item()}>{convertDate(ledger?.startDate || 0)}</div>
          </FormContent>
          <FormContent label='終了日'>
            <div css={generalStyle.item()}>{convertDate(ledger?.endDate || 0)}</div>
          </FormContent>
        </div>
        <FormContent label='請負金額（税抜）'>
          <div css={generalStyle.item({ align: 'right' })}>&yen;{(ledger?.price || 0).toLocaleString()}</div>
        </FormContent>
        <FormContent label='工事内容'>
          <div css={generalStyle.item()}>{ledger?.summary || ''}</div>
        </FormContent>
        <FormContent label='現場代理人'>
          <div css={generalStyle.item()}>{ledger?.leader ? convertUserName(ledger.leader) : ''}</div>
        </FormContent>
        <FormContent label='上長'>
          <div css={generalStyle.item()}>{ledger?.safetyOfficer ? convertUserName(ledger.safetyOfficer) : ''}</div>
        </FormContent>
        <FormContent label='発注元企業'>
          <div css={generalStyle.item()}>{ledger?.orderingParty || 'ー'}</div>
        </FormContent>
        <div css={generalStyle.formRow}>
          <FormContent label='安全書類'>
            <div css={generalStyle.item()}>{safetyDocumentTypeLabel}</div>
          </FormContent>
          <FormContent label='マニフェスト'>
            <div css={generalStyle.item()}>{manifestoType?.label || ''}</div>
          </FormContent>
        </div>
        {flowResource && (
          <FormContent label='産業廃棄物フロー図'>
            <div css={style.resourceItem}>
              <a
                href={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${flowResource.indexName}`}
                download={flowResource.originalName}
                target='_blank'
                css={generalStyle.link}
              >{flowResource.originalName || 'ー'}</a>
              {flowResource.thumbnailName ? (
                <a href={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${flowResource.indexName}`} target='_blank'>
                  <img src={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${flowResource.thumbnailName}`} />
                </a>
              ) : (
                <div />
              )}
            </div>
          </FormContent>
        )}
        <FormContent label='弊社次数'>
          <div css={generalStyle.item()}>{ledger?.order ? `${ledger.order}次` : '元請'}</div>
        </FormContent>
        {ledger?.primeContractor && (
          <FormContent label='元請'>
            <div css={generalStyle.item()}>{ledger.primeContractor.name}</div>
          </FormContent>
        )}
        <div css={style.variableListArea}>
          {(ledger?.members || []).map((member, index) => (
            <div key={index}>
              <FormContent label={`請負協力業者${index + 1}`}>
                <div css={generalStyle.item()}>{member.name}</div>
              </FormContent>
              <FormContent label='請負金額'>
                <div css={generalStyle.item({ align: 'right' })}>&yen;{member.salary.toLocaleString()}</div>
              </FormContent>
            </div>
          ))}
        </div>
        <FormContent label='経費'>
          <div css={style.costArea}>
            <div>
              <div>人工：</div>
              <div>&yen;{totalCosts.personal.toLocaleString()}</div>
            </div>
            <div>
              <div>機械：</div>
              <div>&yen;{totalCosts.macinery.toLocaleString()}</div>
            </div>
            <div>
              <div>産廃費用：</div>
              <div>&yen;{totalCosts.industrialWaste.toLocaleString()}</div>
            </div>
            <div>
              <div>現場経費：</div>
              <div>&yen;{totalCosts.other.toLocaleString()}</div>
            </div>
            <div>
              <div>小計：</div>
              <div>&yen;{totalCosts.total.toLocaleString()}</div>
            </div>
          </div>
        </FormContent>
        <FormContent label='粗利'>
          <div css={generalStyle.item({ align: 'right' })}>&yen;{grossProfit.toLocaleString()}</div>
        </FormContent>
        <FormContent label='備考'>
          <div css={generalStyle.item()}>{ledger?.remarks || ''}</div>
        </FormContent>
        {resources.length > 0 && (
          <FormContent label='添付書類'>
            <div css={style.resourceArea}>
              {resources.map((resourceParam, index) => (
                <div css={style.resourceItem} key={index}>
                  <a
                    href={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${resourceParam.indexName}`}
                    download={resourceParam.originalName}
                    target='_blank'
                    css={generalStyle.link}
                  >{resourceParam.originalName || 'ー'}</a>
                  {resourceParam.thumbnailName ? (
                    <a href={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${resourceParam.indexName}`} target='_blank'>
                      <img src={`${resourceUrl}/C1/${ledger?.resourceCode || ''}/${resourceParam.thumbnailName}`} />
                    </a>
                  ) : (
                    <div />
                  )}
                </div>
              ))}
            </div>
          </FormContent>
        )}
        <div css={generalStyle.formRow}>
          <NavLink to={`/ledger/${ledgerId}/edit`} css={generalStyle.button()}>編集</NavLink>
        </div>
      </div>
      <div css={generalStyle.form}>
        <FormContent label='ステータス'>
          <div css={generalStyle.item()}>{status?.label || ''}</div>
        </FormContent>
        <FormContent label='請求ステータス'>
          <div css={generalStyle.item()}>{paymentStatus?.label || ''}</div>
        </FormContent>
      </div>
    </div>
  </Layout>;
};

export default LedgerShow;
